import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as JobCardActions from './job-card.actions';
import { TechnicianService } from '../../../../core/services/gse/technician.service';

@Injectable()
export class JobCardEffects {
  private actions$ = inject(Actions);
  private technicianService = inject(TechnicianService);

  loadDailyCheckins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.loadDailyCheckins, JobCardActions.loadMoreDailyCheckins),
      switchMap((action) =>
        this.technicianService.getAllJobCards(action.params).pipe(
          map((response) => {
            if (action.type === JobCardActions.loadMoreDailyCheckins.type) {
              return JobCardActions.loadMoreDailyCheckinsSuccess({
                data: response.data || [],
                totalRecords: response.page?.totalElements || 0,
                totalPages: response.page?.totalPages || 0,
                currentPage: response.page?.page || 0,
              });
            }
            return JobCardActions.loadDailyCheckinsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || 0,
              totalPages: response.page?.totalPages || 0,
              currentPage: response.page?.page || 0,
            });
          }),
          catchError((error) =>
            of(
              JobCardActions.loadDailyCheckinsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load daily check-ins',
              })
            )
          )
        )
      )
    )
  );

  loadRefuelingJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.loadRefuelingJobs, JobCardActions.loadMoreRefuelingJobs),
      switchMap((action) =>
        this.technicianService.getAllJobCards(action.params).pipe(
          map((response) => {
            if (action.type === JobCardActions.loadMoreRefuelingJobs.type) {
              return JobCardActions.loadMoreRefuelingJobsSuccess({
                data: response.data || [],
                totalRecords: response.page?.totalElements || 0,
                totalPages: response.page?.totalPages || 0,
                currentPage: response.page?.page || 0,
              });
            }
            return JobCardActions.loadRefuelingJobsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || 0,
              totalPages: response.page?.totalPages || 0,
              currentPage: response.page?.page || 0,
            });
          }),
          catchError((error) =>
            of(
              JobCardActions.loadRefuelingJobsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load refueling jobs',
              })
            )
          )
        )
      )
    )
  );

  loadOtherJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.loadOtherJobs, JobCardActions.loadMoreOtherJobs),
      switchMap((action) =>
        this.technicianService.getAllJobCards(action.params).pipe(
          map((response) => {
            if (action.type === JobCardActions.loadMoreOtherJobs.type) {
              return JobCardActions.loadMoreOtherJobsSuccess({
                data: response.data || [],
                totalRecords: response.page?.totalElements || 0,
                totalPages: response.page?.totalPages || 0,
                currentPage: response.page?.page || 0,
              });
            }
            return JobCardActions.loadOtherJobsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || 0,
              totalPages: response.page?.totalPages || 0,
              currentPage: response.page?.page || 0,
            });
          }),
          catchError((error) =>
            of(
              JobCardActions.loadOtherJobsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load other jobs',
              })
            )
          )
        )
      )
    )
  );

  createRefuelingLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.createRefuelingLog),
      switchMap(({ params }) =>
        this.technicianService.createJobCardRefuelingLog(params).pipe(
          map((response) => JobCardActions.createRefuelingLogSuccess({ response })),
          catchError((error) =>
            of(
              JobCardActions.createRefuelingLogFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to create refueling log',
              })
            )
          )
        )
      )
    )
  );

  loadJobCardById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.loadJobCardById),
      switchMap(({ params }) =>
        this.technicianService.getJobCardById(params).pipe(
          map((response) => JobCardActions.loadJobCardByIdSuccess({ response })),
          catchError((error) =>
            of(
              JobCardActions.loadJobCardByIdFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load job card details',
              })
            )
          )
        )
      )
    )
  );

  loadJobCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobCardActions.loadJobCards),
      switchMap((action) =>
        this.technicianService.getAllJobCards(action.params).pipe(
          map((response) =>
            JobCardActions.loadJobCardsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || 0,
              totalPages: response.page?.totalPages || 0,
              currentPage: response.page?.page || 0,
            })
          ),
          catchError((error) =>
            of(
              JobCardActions.loadJobCardsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load job cards',
              })
            )
          )
        )
      )
    )
  );
}
